type Point = [number, number]
interface Segment {
  from: Point
  to: Point
  controls?: [Point, Point]
}
interface Contour {
  start: Point
  segments: Segment[]
  points: Point[]
}

const same = (a: Point, b: Point) => a[0] === b[0] && a[1] === b[1]
const reflect = (p: Point, around: Point): Point => [
  2 * around[0] - p[0],
  2 * around[1] - p[1],
]

/** Parses arc-free SVG paths into absolute segments; curves stay curves when reversed. */
function contoursOf(d: string): Contour[] {
  const contours: Contour[] = []
  let contour: Contour | undefined
  let current: Point = [0, 0]
  let previous = ''
  let control: Point = current
  const add = (to: Point, controls?: [Point, Point]) => {
    if (!contour) throw new Error('Path must begin with a move')
    contour.segments.push({ from: current, to, controls })
    current = to
  }
  for (const [, command, raw] of d.matchAll(
    /([MmLlHhVvCcSsQqTtZz])([^MmLlHhVvCcSsQqTtZz]*)/g,
  )) {
    const values = (raw.match(/-?(?:\d*\.\d+|\d+)(?:e[-+]?\d+)?/gi) ?? []).map(
      Number,
    )
    const kind = command.toUpperCase()
    const relative = kind !== command
    const stride = (
      { M: 2, L: 2, H: 1, V: 1, C: 6, S: 4, Q: 4, T: 2 } as Record<
        string,
        number
      >
    )[kind]
    if (kind === 'Z') {
      if (contour && !same(current, contour.start)) add(contour.start)
      previous = kind
      continue
    }
    if (!stride || values.length % stride !== 0)
      throw new Error('Invalid path arguments')
    for (let i = 0; i < values.length; i += stride) {
      const p = (offset: number): Point => [
        values[i + offset] + (relative ? current[0] : 0),
        values[i + offset + 1] + (relative ? current[1] : 0),
      ]
      if (kind === 'M' && i === 0) {
        current = p(0)
        contour = { start: current, segments: [], points: [] }
        contours.push(contour)
      } else if (kind === 'M' || kind === 'L') add(p(0))
      else if (kind === 'H')
        add([values[i] + (relative ? current[0] : 0), current[1]])
      else if (kind === 'V')
        add([current[0], values[i] + (relative ? current[1] : 0)])
      else if (kind === 'C') {
        const controls: [Point, Point] = [p(0), p(2)]
        add(p(4), controls)
        control = controls[1]
      } else if (kind === 'S') {
        const controls: [Point, Point] = [
          previous === 'C' || previous === 'S'
            ? reflect(control, current)
            : current,
          p(0),
        ]
        add(p(2), controls)
        control = controls[1]
      } else if (kind === 'Q' || kind === 'T') {
        const q =
          kind === 'Q'
            ? p(0)
            : previous === 'Q' || previous === 'T'
              ? reflect(control, current)
              : current
        const to = p(kind === 'Q' ? 2 : 0)
        const c1: Point = [
          current[0] + (2 / 3) * (q[0] - current[0]),
          current[1] + (2 / 3) * (q[1] - current[1]),
        ]
        const c2: Point = [
          to[0] + (2 / 3) * (q[0] - to[0]),
          to[1] + (2 / 3) * (q[1] - to[1]),
        ]
        add(to, [c1, c2])
        control = q
      }
      previous = kind
    }
  }
  for (const c of contours) {
    c.points.push(c.start)
    for (const segment of c.segments) {
      if (!segment.controls) c.points.push(segment.to)
      else {
        const [a, b] = segment.controls
        // Used only to classify contour nesting and orientation; output retains
        // the original cubic geometry, including the original control points.
        for (let i = 1; i <= 64; i++) {
          const t = i / 64,
            u = 1 - t
          c.points.push(
            [0, 1].map(
              (axis) =>
                u ** 3 * segment.from[axis] +
                3 * u ** 2 * t * a[axis] +
                3 * u * t ** 2 * b[axis] +
                t ** 3 * segment.to[axis],
            ) as Point,
          )
        }
      }
    }
  }
  return contours
}

function contains(points: Point[], point: Point): boolean {
  let inside = false
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i],
      b = points[j]
    if (
      a[1] > point[1] !== b[1] > point[1] &&
      point[0] < ((b[0] - a[0]) * (point[1] - a[1])) / (b[1] - a[1]) + a[0]
    )
      inside = !inside
  }
  return inside
}

/** Non-zero winding equivalent for the artwork's closed, nested contours. */
export function nonZeroPathFromEvenOdd(d: string): string {
  const contours = contoursOf(d)
  const signedArea = (c: Contour) =>
    c.points.reduce((sum, p, i) => {
      const next = c.points[(i + 1) % c.points.length]
      return sum + p[0] * next[1] - next[0] * p[1]
    }, 0)
  return contours
    .map((c) => {
      if (c.segments.length === 0) return `M${c.start.join(' ')}Z`
      const depth = contours.filter(
        (other) => other !== c && contains(other.points, c.start),
      ).length
      const reverse = signedArea(c) > 0 !== (depth % 2 === 0)
      const segments = [...c.segments]
      if (!same(segments[segments.length - 1].to, c.start))
        segments.push({ from: segments[segments.length - 1].to, to: c.start })
      const ordered = reverse
        ? segments
            .reverse()
            .map((s) => ({
              from: s.to,
              to: s.from,
              controls: s.controls
                ? ([s.controls[1], s.controls[0]] as [Point, Point])
                : undefined,
            }))
        : segments
      return (
        `M${c.start.join(' ')} ` +
        ordered
          .map((s) =>
            s.controls
              ? `C${s.controls.flat().join(' ')} ${s.to.join(' ')}`
              : `L${s.to.join(' ')}`,
          )
          .join(' ') +
        ' Z'
      )
    })
    .join(' ')
}

/** Independent coverage checks can sample both source and rewritten contour winding. */
export function pathContains(
  d: string,
  point: Point,
  evenOdd: boolean,
): boolean {
  const contours = contoursOf(d)
  if (evenOdd)
    return contours.filter((c) => contains(c.points, point)).length % 2 === 1
  let winding = 0
  for (const c of contours) {
    for (let i = 0; i < c.points.length; i++) {
      const a = c.points[i],
        b = c.points[(i + 1) % c.points.length]
      const side =
        (b[0] - a[0]) * (point[1] - a[1]) - (point[0] - a[0]) * (b[1] - a[1])
      if (a[1] <= point[1] && b[1] > point[1] && side > 0) winding++
      if (a[1] > point[1] && b[1] <= point[1] && side < 0) winding--
    }
  }
  return winding !== 0
}
